import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Tabs,
  Tab,
  Typography,
} from '@mui/material';
import moment from 'moment';

import CreateFraudTokenForm from './CreateFraudTokenForm';
import { SuspiciousMessageReport } from './SuspiciousMessageReportsTable';

interface ReviewSuspiciousReportDialogProps {
  open: boolean;
  report: SuspiciousMessageReport | null;
  onClose: () => void;
  onCreateToken: (tokenType: string, value: string, notes: string) => void;
  onIgnore: (notes: string) => void;
}

export const ReviewSuspiciousReportDialog: React.FC<
  ReviewSuspiciousReportDialogProps
> = ({ open, report, onClose, onCreateToken, onIgnore }) => {
  const [activeTab, setActiveTab] = useState<'create' | 'ignore'>('ignore');
  const [tokenType, setTokenType] = useState('email');
  const [tokenValue, setTokenValue] = useState('');
  const [tokenNotes, setTokenNotes] = useState('');
  const [ignoreNotes, setIgnoreNotes] = useState('');

  // Reset and pre-fill form when report changes
  useEffect(() => {
    if (report) {
      setActiveTab('create');
      setTokenType('email');
      setTokenValue(report.event.planner?.email || '');
      setTokenNotes(
        `Created from suspicious message report on ${moment(
          report.createdAt
        ).format('MM/DD/YYYY')}`
      );
      setIgnoreNotes('');
    }
  }, [report]);

  // Auto-populate value when token type changes
  useEffect(() => {
    if (report) {
      if (tokenType === 'email') {
        setTokenValue(report.event.planner?.email || '');
      } else if (tokenType === 'phone') {
        setTokenValue(report.event.planner?.phone || '');
      } else if (tokenType === 'message_text') {
        setTokenValue('');
      }
    }
  }, [tokenType, report]);

  const handleCreateToken = () => {
    onCreateToken(tokenType, tokenValue, tokenNotes);
    handleClose();
  };

  const handleIgnore = () => {
    onIgnore(ignoreNotes);
    handleClose();
  };

  const handleClose = () => {
    setActiveTab('ignore');
    setTokenType('email');
    setTokenValue('');
    setTokenNotes('');
    setIgnoreNotes('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Review Suspicious Message Report</DialogTitle>
      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
          >
            <Tab label="Dismiss Report" value="ignore" />
            <Tab label="Create Fraud Token" value="create" />
          </Tabs>
        </Box>

        {activeTab === 'create' && (
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Create a fraud token to automatically flag future events with this
              planner's information.
            </Typography>
            <CreateFraudTokenForm
              tokenType={tokenType}
              value={tokenValue}
              notes={tokenNotes}
              onTokenTypeChange={setTokenType}
              onValueChange={setTokenValue}
              onNotesChange={setTokenNotes}
            />
          </Box>
        )}
        {activeTab === 'ignore' && (
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Dismiss this report if you've reviewed it and no action needs to
              be taken. You can optionally add notes explaining why.
            </Typography>
            <TextField
              label="Notes (optional)"
              value={ignoreNotes}
              onChange={(e) => setIgnoreNotes(e.target.value)}
              fullWidth
              multiline
              rows={4}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="primary" onClick={handleClose}>
          Cancel
        </Button>
        {activeTab === 'create' && (
          <Button
            onClick={handleCreateToken}
            variant="contained"
            disabled={!tokenValue}
          >
            Create Token
          </Button>
        )}
        {activeTab === 'ignore' && (
          <Button onClick={handleIgnore} variant="contained" color="primary">
            Dismiss Report
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
